import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type {
  CreateSiteInput,
  SiteStatus,
  SiteType,
} from '@dsr/shared-types';
import {
  useCreateSite,
  useSites,
  type SiteFilters,
} from '../api/queries/sites';
import { useOrganizations } from '../api/queries/clients';
import { toast } from '../components/Toasts';
import { can } from '../auth/store';
import { ApiError } from '../api/client';
import {
  Button,
  Dialog,
  FormField,
  Input,
  Select,
  StatusBadge,
  Table,
  Textarea,
  type SelectOption,
} from '../components/ui';

const SITE_TYPES: SiteType[] = [
  'fixed_post',
  'patrol',
  'residential',
  'commercial',
  'event_venue',
  'construction',
  'retail',
];
const SITE_STATUSES: SiteStatus[] = ['active', 'inactive', 'survey'];

export const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV',
  'NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN',
  'TX','UT','VT','VA','WA','WV','WI','WY','DC','PR','VI','GU','AS','MP',
] as const;

export function SitesPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const orgFromUrl = searchParams.get('organization_id');
  const initialOrgId = orgFromUrl ? Number(orgFromUrl) : undefined;

  const [filters, setFilters] = useState<SiteFilters>(
    initialOrgId ? { organization_id: initialOrgId } : {},
  );
  const [qDraft, setQDraft] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, isError, error } = useSites(filters);
  const { data: orgData } = useOrganizations();

  const canWrite = can('site.write');
  const sites = data?.sites ?? [];

  const orgNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const o of orgData?.organizations ?? []) map.set(o.id, o.name);
    return map;
  }, [orgData]);

  // Standalone filter selects: Radix Select forbids an empty value, so 'all'
  // is the no-filter sentinel and is mapped back to undefined on change.
  const orgFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All clients' },
    ...(orgData?.organizations ?? []).map((o) => ({
      value: String(o.id),
      label: o.name,
    })),
  ];
  const statusFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All statuses' },
    ...SITE_STATUSES.map((s) => ({ value: s, label: s })),
  ];
  const typeFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All types' },
    ...SITE_TYPES.map((t) => ({ value: t, label: t })),
  ];

  return (
    <div className="page sites-page">
      <header className="page-header">
        <div>
          <h1>Sites</h1>
          <p className="page-sub">Physical posting locations.</p>
        </div>
        {canWrite && (
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            New site
          </Button>
        )}
      </header>

      <div className="filters-bar">
        <form
          className="filter-search"
          onSubmit={(e) => {
            e.preventDefault();
            setFilters((f) => ({ ...f, q: qDraft }));
          }}
        >
          <input
            type="search"
            className="form-input"
            placeholder="Search by name…"
            value={qDraft}
            onChange={(e) => setQDraft(e.target.value)}
          />
        </form>

        <Select
          value={filters.organization_id !== undefined ? String(filters.organization_id) : 'all'}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              organization_id: v !== 'all' ? Number(v) : undefined,
            }))
          }
          options={orgFilterOptions}
          aria-label="Filter by client"
        />

        <Select
          value={filters.status ?? 'all'}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              status: v !== 'all' ? (v as SiteStatus) : undefined,
            }))
          }
          options={statusFilterOptions}
          aria-label="Filter by status"
        />

        <Select
          value={filters.site_type ?? 'all'}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              site_type: v !== 'all' ? (v as SiteType) : undefined,
            }))
          }
          options={typeFilterOptions}
          aria-label="Filter by type"
        />
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {isError && (
        <p className="error-text" role="alert">
          {error instanceof ApiError ? error.message : 'Failed to load sites.'}
        </p>
      )}
      {!isLoading && !isError && sites.length === 0 && (
        <p className="muted">No sites match the current filters.</p>
      )}

      {sites.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Client</Table.HeaderCell>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Address</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {sites.map((s) => (
              <Table.Row key={s.id}>
                <Table.Cell tone="strong">
                  <Link to={`/sites/${s.id}`} className="table-link">
                    {s.name}
                  </Link>
                </Table.Cell>
                <Table.Cell>
                  {orgNameById.get(s.organization_id) ?? `#${s.organization_id}`}
                </Table.Cell>
                <Table.Cell>{s.site_type}</Table.Cell>
                <Table.Cell>
                  <StatusBadge status={s.status}>{s.status}</StatusBadge>
                </Table.Cell>
                <Table.Cell tone="muted">{s.address ?? '—'}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {createOpen && (
        <CreateSiteModal
          defaultOrgId={filters.organization_id}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </div>
  );
}

function CreateSiteModal({
  defaultOrgId,
  onClose,
}: {
  defaultOrgId?: number;
  onClose: () => void;
}): JSX.Element {
  const { data: orgData } = useOrganizations();
  const [organizationId, setOrganizationId] = useState<number | ''>(
    defaultOrgId ?? '',
  );
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [state, setState] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [radius, setRadius] = useState('');
  const [siteType, setSiteType] = useState<SiteType>('commercial');
  const [status, setStatus] = useState<SiteStatus>('active');
  const [notes, setNotes] = useState('');

  const create = useCreateSite();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (organizationId === '') {
      toast.warn('Select a client.');
      return;
    }
    const input: CreateSiteInput = {
      organization_id: organizationId,
      name: name.trim(),
      site_type: siteType,
      status,
    };
    if (address.trim()) input.address = address.trim();
    if (state) input.state = state;
    if (lat.trim()) input.lat = lat.trim();
    if (lng.trim()) input.lng = lng.trim();
    if (radius.trim()) input.geofence_radius_m = Number(radius);
    if (notes.trim()) input.notes = notes.trim();

    try {
      await create.mutateAsync(input);
      toast.success('Site created.');
      onClose();
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to create site.');
    }
  };

  return (
    <Dialog
      title="New site"
      open
      onClose={onClose}
      busy={create.isPending}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-site-form"
            variant="primary"
            disabled={
              create.isPending || name.trim().length === 0 || organizationId === ''
            }
          >
            {create.isPending ? 'Saving…' : 'Create'}
          </Button>
        </>
      }
    >
      <form id="create-site-form" className="form-grid" onSubmit={onSubmit}>
        <FormField label="Client" required>
          <select
            className="form-input"
            value={organizationId}
            onChange={(e) =>
              setOrganizationId(e.target.value ? Number(e.target.value) : '')
            }
            required
          >
            <option value="">Select a client…</option>
            {(orgData?.organizations ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Name" required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </FormField>
        <FormField label="Type">
          <select
            className="form-input"
            value={siteType}
            onChange={(e) => setSiteType(e.target.value as SiteType)}
          >
            {SITE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Status">
          <select
            className="form-input"
            value={status}
            onChange={(e) => setStatus(e.target.value as SiteStatus)}
          >
            {SITE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </FormField>
        <label className="form-field form-field-full">
          <span>Address</span>
          <Input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </label>
        <FormField label="State">
          <select
            className="form-input"
            value={state}
            onChange={(e) => setState(e.target.value)}
          >
            <option value="">—</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Latitude">
          <Input
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            placeholder="40.712776"
            inputMode="decimal"
          />
        </FormField>
        <FormField label="Longitude">
          <Input
            value={lng}
            onChange={(e) => setLng(e.target.value)}
            placeholder="-74.005974"
            inputMode="decimal"
          />
        </FormField>
        <FormField label="Geofence radius (m)">
          <Input
            type="number"
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
            placeholder="150"
            min={1}
          />
        </FormField>
        <label className="form-field form-field-full">
          <span>Notes</span>
          <Textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
      </form>
    </Dialog>
  );
}
